import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardList, Plus, Upload, CheckCircle, Clock, AlertCircle, User, FileText, Star, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseLogPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [caseLogs, setCaseLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    patient_name: '',
    patient_age: '',
    patient_gender: 'male',
    procedure_type: '',
    diagnosis: '',
    treatment_plan: '',
    technique: '',
    products_used: '',
    complications: false,
    complication_details: '',
    outcome: '',
    supervisor_name: '',
    notes: '',
  });
  const [patientPhoto, setPatientPhoto] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCaseLogs();
    }
  }, [token]);

  const fetchCaseLogs = async () => {
    try {
      const res = await axios.get(`${API}/my-case-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseLogs(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.procedure_type || !form.diagnosis || !form.treatment_plan || !form.technique || !form.outcome) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(form).forEach(([key, value]) => {
        params.append(key, value.toString());
      });

      const formData = new FormData();
      if (patientPhoto) {
        formData.append('patient_photo', patientPhoto);
      }

      await axios.post(`${API}/case-logs/upload?${params.toString()}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Case log submitted successfully');
      setShowForm(false);
      setForm({
        patient_name: '',
        patient_age: '',
        patient_gender: 'male',
        procedure_type: '',
        diagnosis: '',
        treatment_plan: '',
        technique: '',
        products_used: '',
        complications: false,
        complication_details: '',
        outcome: '',
        supervisor_name: '',
        notes: '',
      });
      setPatientPhoto(null);
      fetchCaseLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit case log');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Submitted' },
      assigned: { bg: 'bg-amber-100 text-amber-700', icon: User, label: 'Assigned' },
      scored: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Scored' },
    };
    const style = styles[status] || styles.submitted;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} rounded-sm px-2 py-0.5 text-xs`}>
        <Icon className="h-3 w-3 mr-1" />{style.label}
      </Badge>
    );
  };

  const procedureTypes = [
    'Botulinum Toxin Injection',
    'Dermal Filler - Lips',
    'Dermal Filler - Cheeks',
    'Dermal Filler - Nasolabial',
    'Dermal Filler - Chin',
    'Thread Lift',
    'PRP Treatment',
    'Mesotherapy',
    'Laser Treatment',
    'Chemical Peel',
    'Microneedling',
    'IV Therapy',
    'Other'
  ];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="case-log-page">
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="text-white hover:bg-white/10 mb-4 -ml-2"
            data-testid="case-log-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
          <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Clinical Documentation</p>
          <h1 className="text-3xl font-bold text-white tracking-tight font-heading">Case Log Portfolio</h1>
          <p className="text-slate-300 text-sm mt-1">Document your clinical procedures for competency assessment</p>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-blue-100 text-blue-600">
                <ClipboardList className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{caseLogs.length}</p>
                <p className="text-xs text-slate-500">Total Logs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-green-100 text-green-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{caseLogs.filter(l => l.status === 'scored').length}</p>
                <p className="text-xs text-slate-500">Scored</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-amber-100 text-amber-600">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{caseLogs.filter(l => l.status !== 'scored').length}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-purple-100 text-purple-600">
                <Star className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A] font-heading">
                  {caseLogs.filter(l => l.scoring).length > 0 
                    ? (caseLogs.filter(l => l.scoring).reduce((acc, l) => acc + (l.scoring?.overall_score || 0), 0) / caseLogs.filter(l => l.scoring).length).toFixed(1)
                    : '-'}
                </p>
                <p className="text-xs text-slate-500">Avg Score</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* New Case Log Button */}
        <div className="flex justify-end mb-4">
          <Button onClick={() => setShowForm(!showForm)} className="bg-[#1B2A4A] text-white rounded-sm" data-testid="new-case-log-btn">
            <Plus className="h-4 w-4 mr-1" /> New Case Log
          </Button>
        </div>

        {/* New Case Log Form */}
        {showForm && (
          <Card className="imcac-card mb-6 border-2 border-[#C5A572]" data-testid="case-log-form">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-6">Submit New Case Log</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Patient Information */}
                <div className="border-b border-slate-200 pb-4">
                  <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3 flex items-center"><User className="h-4 w-4 mr-2" />Patient Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs">Patient Name *</Label>
                      <Input value={form.patient_name} onChange={e => setForm({...form, patient_name: e.target.value})} className="mt-1 rounded-sm" placeholder="Patient initials or ID" required data-testid="patient-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Age *</Label>
                      <Input type="number" value={form.patient_age} onChange={e => setForm({...form, patient_age: e.target.value})} className="mt-1 rounded-sm" placeholder="Age" required data-testid="patient-age" />
                    </div>
                    <div>
                      <Label className="text-xs">Gender *</Label>
                      <Select value={form.patient_gender} onValueChange={v => setForm({...form, patient_gender: v})}>
                        <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Patient Photo</Label>
                      <Input type="file" accept="image/*" onChange={e => setPatientPhoto(e.target.files?.[0] || null)} className="mt-1 rounded-sm text-xs" data-testid="patient-photo" />
                    </div>
                  </div>
                </div>

                {/* Procedure Details */}
                <div className="border-b border-slate-200 pb-4">
                  <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3 flex items-center"><FileText className="h-4 w-4 mr-2" />Procedure Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Procedure Type *</Label>
                      <Select value={form.procedure_type} onValueChange={v => setForm({...form, procedure_type: v})}>
                        <SelectTrigger className="mt-1 rounded-sm" data-testid="procedure-type"><SelectValue placeholder="Select procedure" /></SelectTrigger>
                        <SelectContent>
                          {procedureTypes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Diagnosis *</Label>
                      <Input value={form.diagnosis} onChange={e => setForm({...form, diagnosis: e.target.value})} className="mt-1 rounded-sm" placeholder="Primary diagnosis" required data-testid="diagnosis" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Treatment Plan *</Label>
                      <textarea value={form.treatment_plan} onChange={e => setForm({...form, treatment_plan: e.target.value})} className="w-full mt-1 p-2 border border-slate-200 rounded-sm text-sm" rows={2} placeholder="Describe the treatment plan" required data-testid="treatment-plan" />
                    </div>
                    <div>
                      <Label className="text-xs">Technique *</Label>
                      <Input value={form.technique} onChange={e => setForm({...form, technique: e.target.value})} className="mt-1 rounded-sm" placeholder="Technique used" required data-testid="technique" />
                    </div>
                    <div>
                      <Label className="text-xs">Products Used</Label>
                      <Input value={form.products_used} onChange={e => setForm({...form, products_used: e.target.value})} className="mt-1 rounded-sm" placeholder="Products, dosage, etc." data-testid="products-used" />
                    </div>
                  </div>
                </div>

                {/* Outcome */}
                <div className="border-b border-slate-200 pb-4">
                  <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3">Outcome & Complications</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Outcome *</Label>
                      <Select value={form.outcome} onValueChange={v => setForm({...form, outcome: v})}>
                        <SelectTrigger className="mt-1 rounded-sm" data-testid="outcome"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Excellent">Excellent</SelectItem>
                          <SelectItem value="Good">Good</SelectItem>
                          <SelectItem value="Satisfactory">Satisfactory</SelectItem>
                          <SelectItem value="Poor">Poor</SelectItem>
                          <SelectItem value="Complications">Complications Occurred</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3 mt-6">
                      <input type="checkbox" checked={form.complications} onChange={e => setForm({...form, complications: e.target.checked})} className="rounded" id="complications" />
                      <Label htmlFor="complications" className="text-sm">Complications occurred?</Label>
                    </div>
                    {form.complications && (
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Complication Details</Label>
                        <textarea value={form.complication_details} onChange={e => setForm({...form, complication_details: e.target.value})} className="w-full mt-1 p-2 border border-slate-200 rounded-sm text-sm" rows={2} placeholder="Describe complications and management" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Supervisor Name</Label>
                      <Input value={form.supervisor_name} onChange={e => setForm({...form, supervisor_name: e.target.value})} className="mt-1 rounded-sm" placeholder="Supervising physician" />
                    </div>
                    <div>
                      <Label className="text-xs">Additional Notes</Label>
                      <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-sm" placeholder="Any additional notes" />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-[#C5A572] text-white rounded-sm" disabled={submitting} data-testid="submit-case-log">
                    {submitting ? 'Submitting...' : 'Submit Case Log'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-sm">Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Case Logs List */}
        <Card className="imcac-card">
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Your Case Logs</h3>
            {loading ? (
              <p className="text-sm text-slate-500 py-8 text-center">Loading...</p>
            ) : caseLogs.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Case Logs Yet</h4>
                <p className="text-sm text-slate-500 mb-4">Start documenting your clinical procedures</p>
                <Button onClick={() => setShowForm(true)} className="bg-[#C5A572] text-white rounded-sm">
                  Submit Your First Case
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F8FAFC]">
                      <TableHead className="text-xs font-bold">Date</TableHead>
                      <TableHead className="text-xs font-bold">Patient</TableHead>
                      <TableHead className="text-xs font-bold">Procedure</TableHead>
                      <TableHead className="text-xs font-bold">Outcome</TableHead>
                      <TableHead className="text-xs font-bold">Status</TableHead>
                      <TableHead className="text-xs font-bold">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {caseLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-xs text-slate-500">{new Date(log.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <p className="text-sm font-medium text-[#1B2A4A]">{log.patient_name}</p>
                          <p className="text-xs text-slate-500">{log.patient_age}y, {log.patient_gender}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs rounded-sm">{log.procedure_type}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{log.outcome}</TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          {log.scoring ? (
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                              <span className="text-sm font-semibold">{log.scoring.overall_score}/10</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
